<template>
  <SearchSheet :open="open" :anchor-rect="anchorRect" aria-label="エリア・絞り込みの選択" full-height @close="$emit('close')">
    <template #head>
      <div class="top-search-panel-field md:hidden">
        <slot name="field"></slot>
      </div>
      <div class="top-search-panel-head">
        <div class="flex w-full">
          <button
            type="button"
            class="top-search-panel-tab"
            :class="{ active: activeTab === 'area' }"
            @click.prevent="activeTab = 'area'"
          >
            <icon-area-pin />
            エリア
          </button>
          <button
            type="button"
            class="top-search-panel-tab"
            :class="{ active: activeTab === 'tag' }"
            @click.prevent="activeTab = 'tag'"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M20.59 13.41l-7.17 7.17a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z"></path>
              <line x1="7" y1="7" x2="7.01" y2="7"></line>
            </svg>
            人気のタグ
          </button>
          <button
            type="button"
            class="top-search-panel-tab"
            :class="{ active: activeTab === 'detail' }"
            @click.prevent="activeTab = 'detail'"
          >
            <icon-setting />
            絞り込み
          </button>
        </div>
      </div>
    </template>

    <div v-show="activeTab === 'area'">
      <div v-for="area in areas" :key="'panel-area-' + area.id" class="top-search-panel-area">
        <div class="top-search-panel-area-row">
          <div class="top-search-panel-area-check">
            <input
              :id="'panel_area_' + area.id"
              type="checkbox"
              :checked="checkedAreas.includes(area.id)"
              @change="$emit('areaChanged', $event.target.checked, area)"
            />
            <label :for="'panel_area_' + area.id">{{ area.name }}</label>
          </div>
          <button
            type="button"
            class="top-search-panel-area-toggle"
            :class="{ open: expandedAreas.includes(area.id) }"
            :aria-expanded="expandedAreas.includes(area.id) ? 'true' : 'false'"
            :aria-label="area.name + 'の都道府県を' + (expandedAreas.includes(area.id) ? '閉じる' : '開く')"
            @click.prevent="toggleArea(area.id)"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </button>
        </div>
        <div v-show="expandedAreas.includes(area.id)" class="top-search-panel-prefs">
          <div v-for="pref in area.prefectures" :key="'panel-pref-' + pref.id" class="top-search-panel-pref">
            <input
              :id="'panel_pref_' + pref.id"
              type="checkbox"
              :checked="checkedPrefs.includes(pref.id)"
              @change="$emit('prefChanged', $event.target.checked, pref)"
            />
            <label :for="'panel_pref_' + pref.id">{{ pref.name }}</label>
          </div>
        </div>
      </div>
    </div>

    <div v-show="activeTab === 'tag'" class="top-search-panel-tags">
      <p class="top-search-panel-tags-note">タグは1つだけ選べます</p>
      <div class="top-search-panel-tags-list">
        <button
          v-for="tag in tags"
          :key="'panel-tag-' + tag.id"
          type="button"
          class="top-search-tag"
          :class="{ checked: checkedTag === tag.id }"
          :aria-pressed="checkedTag === tag.id ? 'true' : 'false'"
          @click.prevent="$emit('tagChanged', tag.id)"
        >
          {{ tag.name }}
        </button>
      </div>
    </div>

    <div v-show="activeTab === 'detail'" class="modal-search-filter top-search-panel-filter">
      <DetailFilterFields
        :home-kinds="homeKinds"
        :room-kinds="roomKinds"
        :home-group-kinds="homeGroupKinds"
        :checked-home-kinds="checkedHomeKinds"
        :checked-room-kinds="checkedRoomKinds"
        :is-original="isOriginal"
        :has-kitchen="hasKitchen"
        :has-parking="hasParking"
        :is-parking-free="isParkingFree"
        :is-parking-reservable="isParkingReservable"
        :has-desk-chair="hasDeskChair"
        :can-meet-yamori="canMeetYamori"
        :is-member-only="isMemberOnly"
        :checked-home-group-kinds="checkedHomeGroupKinds"
        :has-auto-approval="hasAutoApproval"
        :has-discount="hasDiscount"
        :has-discount-three-nights="hasDiscountThreeNights"
        :has-discount-seven-nights="hasDiscountSevenNights"
        :favorite-only="favoriteOnly"
        :is-contracted="isContracted"
        :is-legacy-plan="isLegacyPlan"
        :is-unregistered="isUnregistered"
        :icon-original-img-src="iconOriginalImgSrc"
        @checkChanged="(key, isChecked) => $emit('checkChanged', key, isChecked)"
        @homeKindsChanged="(isSelected, kind) => $emit('homeKindsChanged', isSelected, kind)"
        @roomKindsChanged="(isSelected, kind) => $emit('roomKindsChanged', isSelected, kind)"
        @hasParkingChanged="(isChecked) => $emit('hasParkingChanged', isChecked)"
        @parkingDetailChanged="(event) => $emit('parkingDetailChanged', event)"
        @homeGroupKindsChanged="(isSelected, kind) => $emit('homeGroupKindsChanged', isSelected, kind)"
        @hasDiscountChanged="(isChecked) => $emit('hasDiscountChanged', isChecked)"
        @discountDetailChanged="(event) => $emit('discountDetailChanged', event)"
      />
    </div>

    <template #foot>
      <button type="button" class="top-search-panel-reset" @click.prevent="reset">
        {{ resetLabel }}
      </button>
      <button type="button" class="top-search-panel-submit" @click.prevent="$emit('close')">決定</button>
    </template>
  </SearchSheet>
</template>

<script>
import SearchSheet from './SearchSheet.vue'
import DetailFilterFields from '../../shared/components/search/DetailFilterFields.vue'
import IconAreaPin from '../../shared/icons/AreaPin.vue'
import IconSetting from '../../shared/icons/Setting.vue'

export default {
  components: {
    SearchSheet,
    DetailFilterFields,
    IconAreaPin,
    IconSetting,
  },
  props: {
    ...DetailFilterFields.props,
    areas: {
      type: Array,
      required: true,
    },
    checkedAreas: {
      type: Array,
      required: true,
    },
    checkedPrefs: {
      type: Array,
      required: true,
    },
    tags: {
      type: Array,
      required: true,
    },
    checkedTag: {
      type: Number,
      default: null,
    },
    open: {
      type: Boolean,
      required: true,
    },
    // Viewport-relative box of the input the panel hangs from (desktop only).
    anchorRect: {
      type: Object,
      default: null,
    },
  },
  data() {
    return {
      activeTab: 'area',
      expandedAreas: [],
    }
  },
  computed: {
    resetLabel() {
      return { area: 'エリアをクリア', tag: 'タグをクリア', detail: '絞り込みをクリア' }[this.activeTab]
    },
  },
  methods: {
    // Called by the parent each time the panel is shown: pick the tab and expand areas that already have a selection.
    show(tab) {
      this.activeTab = tab
      const withSelection = this.areas
        .filter((area) => area.prefectures.some((pref) => this.checkedPrefs.includes(pref.id)))
        .map((area) => area.id)
      this.expandedAreas = [...new Set([...this.expandedAreas, ...withSelection])]
    },
    toggleArea(areaId) {
      if (this.expandedAreas.includes(areaId)) {
        this.expandedAreas = this.expandedAreas.filter((id) => id !== areaId)
      } else {
        this.expandedAreas = [...this.expandedAreas, areaId]
      }
    },
    reset() {
      if (this.activeTab === 'area') {
        this.$emit('clearArea')
      } else if (this.activeTab === 'tag') {
        this.$emit('clearTag')
      } else {
        this.$emit('clearDetail')
      }
    },
  },
}
</script>
